import { AppSelect } from '../../ui/AppSelect';
import { isDomesticOfficialSeedance, getDomesticSeedanceResolutions, normalizeDomesticSeedanceResolution } from '../../../utils/domesticSeedanceCapabilities';
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown, Check } from "lucide-react";
import { CANVAS_VIDEO_MODELS as VIDEO_MODELS } from "../utils/videoModels";
import { getSeedance25Capabilities, normalizeSeedance25Resolution } from "../../../utils/seedance25Capabilities";
import { LIGHTING_PRESETS } from "../../storyboard/common/LightingPresets";
import {
  getGuaOminiVideoResolutions,
  getProjectVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoResolutionForProvider,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
} from "../../../utils/projectRuntime";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import {
  isWanxVideoProvider,
  normalizeWanxVideoResolution,
  WANX_VIDEO_RATIOS,
  WANX_VIDEO_RESOLUTIONS,
} from "../../../utils/wanxVideoCapabilities";

const ACCENT = 'var(--color-accent-primary)';
// 主题变量无法拼 hex-alpha，统一用 color-mix 生成浅色底
const accentBg = (color, pct = 12) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

const getProviderConfig = (providerId) => VIDEO_MODELS.find(m => m.id === providerId);

const ASPECT_RATIOS = ["16:9", "21:9", "9:16", "1:1", "4:3", "3:4"];
const VIDEO_RESOLUTIONS = ["480p", "720p", "1080p"];

// 推荐组：只展示当前项目固定的视频线路。
const RECOMMENDED_PROVIDERS = [
  { id: "guavideo", badge: "国内", badgeColor: "var(--color-accent-secondary)" },
  { id: "engguavideo", badge: "出海", badgeColor: "var(--color-success)" },
  { id: "omini_seedance", badge: "出海", badgeColor: "var(--color-info)" },
];

const GUA_VIDEO_MODES = [
  { id: "ref2v", name: "参考生视频" },
  { id: "i2v", name: "图生视频" },
  { id: "t2v", name: "文生视频" },
];

const WANX_VIDEO_MODES = [
  { id: "text2video", name: "文生视频" },
  { id: "img2video", name: "首帧生视频" },
  { id: "start-end", name: "首尾帧生视频" },
  { id: "reference", name: "参考生视频" },
];

// 分区标题
const Section = ({ title, children, first = false }) => (
  <div
    className="px-4 py-3.5"
    style={first ? undefined : { borderTop: "1px solid var(--color-border-secondary)" }}
  >
    <div className="text-label mb-2">{title}</div>
    {children}
  </div>
);

// 通用小 pill 按钮
const Pill = ({ active, color = ACCENT, onClick, children, mono = true }) => (
  <button
    onClick={onClick}
    className={`px-2 h-6 rounded-md text-2xs font-medium whitespace-nowrap transition-colors ${mono ? "font-mono-num" : ""}`}
    style={{
      background: active ? accentBg(color) : "transparent",
      color: active ? color : "var(--color-text-muted)",
      border: `1px solid ${active ? accentBg(color, 40) : "var(--color-border-secondary)"}`,
    }}
  >
    {children}
  </button>
);

/**
 * 视频生成设置弹框（工作台风格）
 * 状态全部受控于 AtomEditPanel：弹框内改动即时写回面板 state，
 * 生成时仍走面板现有 buildPayload/onUpdate/onGenerate 同步机制。
 */
export default function GenerateSettingsModal({
  open,
  onClose,
  projectId,
  provider,
  onProviderChange,
  subModel,
  onSubModelChange,
  duration,
  onDurationChange,
  aspectRatio,
  onAspectRatioChange,
  videoResolution,
  onVideoResolutionChange,
  guaVideoMode,
  onGuaVideoModeChange,
  seedance25Mode,
  onSeedance25ModeChange,
  guaAudio,
  onGuaAudioChange,
  guaWebSearch,
  onGuaWebSearchChange,
  wanxMode,
  onWanxModeChange,
  wanxAudio,
  onWanxAudioChange,
  wanxWatermark,
  onWanxWatermarkChange,
  wanxSeed,
  onWanxSeedChange,
  selectedLighting,
  onLightingChange,
}) {
  const [othersOpen, setOthersOpen] = useState(false); // “其他模型”默认折叠
  const { mounted, exiting } = useDelayedUnmount(open, 150);
  const othersAnim = useDelayedUnmount(othersOpen, 150);
  const isWanx = isWanxVideoProvider(provider);
  const seedance25Capabilities = getSeedance25Capabilities(provider);
  const videoResolutions = isWanx
    ? WANX_VIDEO_RESOLUTIONS
    : isDomesticOfficialSeedance(provider)
      ? getDomesticSeedanceResolutions(subModel)
    : seedance25Capabilities
      ? seedance25Capabilities.resolutions
      : provider === VIDEO_PROVIDER_OMINI_SEEDANCE
        ? getGuaOminiVideoResolutions()
        : VIDEO_RESOLUTIONS;
  const aspectRatios = isWanx ? WANX_VIDEO_RATIOS : seedance25Capabilities?.ratios || ASPECT_RATIOS;

  useEffect(() => {
    const normalized = isWanx
      ? normalizeWanxVideoResolution(videoResolution)
      : isDomesticOfficialSeedance(provider)
        ? normalizeDomesticSeedanceResolution(subModel, videoResolution)
      : seedance25Capabilities
        ? normalizeSeedance25Resolution(provider, videoResolution)
        : normalizeVideoResolutionForProvider(provider, videoResolution, "720p");
    if (normalized !== videoResolution) onVideoResolutionChange(normalized);
  }, [isWanx, onVideoResolutionChange, provider, subModel, seedance25Capabilities, videoResolution]);

  // Esc 关闭
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const providerConfig = getProviderConfig(provider);
  const subModels = providerConfig?.subModels || [];
  const currentSubModel = subModels.find(m => m.id === subModel) || subModels[0];
  const projectDefaultProvider = getProjectVideoProvider(projectId);
  // 国内/出海线路按项目市场互斥：国内项目不显示 EngGuaVideo，海外项目不显示 GuaVideo
  const visibleRecommended = RECOMMENDED_PROVIDERS.filter(rp => rp.id === projectDefaultProvider);

  // 时长数据：优先子模型 > 模型级别 > 默认（与面板原逻辑一致）
  const durationsList = currentSubModel?.durations || providerConfig?.durations || null;
  const durationRange = currentSubModel?.durationRange || null;
  const hasDurationControl = !!(durationsList || durationRange);
  const fallbackDurations = !hasDurationControl && providerConfig?.defaultDuration
    ? [providerConfig.defaultDuration]
    : null;

  // 其他模型 = 全部 provider 去掉推荐组两个
  const recommendedIds = RECOMMENDED_PROVIDERS.map(p => p.id);
  const otherProviders = VIDEO_MODELS
    .filter(m => !recommendedIds.includes(m.id))
    .map(m => ({ id: m.id, name: m.name, color: m.color || m.accentColor || ACCENT }));

  const isRecommendedProvider = recommendedIds.includes(provider);

  const modal = (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center nodrag nowheel ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-150"}`}
      style={{ background: "var(--overlay-medium)" }}
      onMouseDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={`rounded-xl w-[460px] max-w-[92vw] max-h-[80vh] flex flex-col overflow-hidden ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{
          background: "var(--color-bg-secondary)",
          border: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-modal)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>生成设置</span>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
            style={{ color: "var(--color-text-muted)", background: "var(--color-bg-tertiary)" }}
            title="关闭"
          >
            <X size={13} />
          </button>
        </div>

        {/* 内容区（滚动） */}
        <div className="overflow-y-auto">
          {/* ── 模型 ── */}
          <Section title="模型" first>
            {seedance25Capabilities?.retired && <p role="status" className="mb-2 text-xs" style={{ color: 'var(--color-warning)' }}>当前历史 2.5 线路已停止新生成，请选择国内 Seedance 或海外 Dreamina；原参考连线保持不变。</p>}
            {/* 推荐组：按项目市场只显示国内 GuaVideo 或出海 EngGuaVideo */}
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${visibleRecommended.length || 1}, minmax(0, 1fr))` }}>
              {visibleRecommended.map(rp => {
                const cfg = getProviderConfig(rp.id);
                if (!cfg) return null;
                const isSel = provider === rp.id;
                const isProjectDefault = projectDefaultProvider === rp.id;
                return (
                  <button
                    key={rp.id}
                    onClick={() => onProviderChange(rp.id)}
                    className="flex flex-col items-start gap-1 px-3 py-2.5 rounded-lg text-left transition-colors"
                    style={{
                      background: isSel ? accentBg(rp.badgeColor, 10) : "var(--color-bg-tertiary)",
                      border: `1px solid ${isSel ? accentBg(rp.badgeColor, 45) : "var(--color-border-secondary)"}`,
                    }}
                  >
                    <div className="flex items-center gap-1.5 w-full">
                      <span className="text-xs font-semibold" style={{ color: isSel ? rp.badgeColor : "var(--color-text-primary)" }}>
                        {cfg.name}
                      </span>
                      {isSel && <Check size={11} style={{ color: rp.badgeColor }} />}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className="text-2xs px-1.5 py-0.5 rounded"
                        style={{ color: rp.badgeColor, background: accentBg(rp.badgeColor, 12) }}
                      >
                        {rp.badge}
                      </span>
                      {isProjectDefault && (
                        <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>项目默认</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 其他模型：默认折叠 */}
            <button
              onClick={() => setOthersOpen(!othersOpen)}
              className="flex items-center gap-1 mt-2 px-1 h-6 text-2xs font-medium transition-colors"
              style={{ color: !isRecommendedProvider ? ACCENT : "var(--color-text-muted)" }}
            >
              <ChevronDown size={10} className={`transition-transform ${othersOpen ? "rotate-180" : ""}`} />
              其他模型
              {!isRecommendedProvider && (
                <span style={{ color: ACCENT }}>· {getProviderConfig(provider)?.name || provider}</span>
              )}
            </button>
            {othersAnim.mounted && (
              <div className={`flex items-center gap-1 flex-wrap mt-1 ${othersAnim.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-150"}`}>
                {otherProviders.map(p => (
                  <Pill key={p.id} active={provider === p.id} color={p.color} mono={false} onClick={() => onProviderChange(p.id)}>
                    {p.name}
                  </Pill>
                ))}
              </div>
            )}

            {/* 子模型 */}
            {subModels.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap mt-2.5">
                <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>子模型</span>
                {subModels.map(sm => (
                  <Pill
                    key={sm.id}
                    active={(subModel || currentSubModel?.id) === sm.id}
                    mono={false}
                    onClick={() => onSubModelChange(sm.id)}
                  >
                    {sm.name}
                  </Pill>
                ))}
              </div>
            )}
          </Section>

          {/* ── 时长 ── */}
          {(durationsList || durationRange || fallbackDurations) && (
            <Section title="时长">
              {(durationsList || fallbackDurations) ? (
                (durationsList || fallbackDurations).length > 5 ? (
                  <AppSelect
                    value={duration}
                    onChange={(e) => onDurationChange(Number(e.target.value))}
                    className="px-2 h-7 rounded-md text-2xs font-medium font-mono-num outline-none"
                    style={{ background: accentBg(ACCENT), color: ACCENT, border: "none" }}
                  >
                    {(durationsList || fallbackDurations).map(d => {
                      const dNum = typeof d === 'string' ? parseInt(d) : d;
                      const label = dNum === -1 ? '智能时长' : (typeof d === 'string' ? d : `${d}s`);
                      return <option key={d} value={dNum} style={{ background: "var(--color-bg-secondary)", color: "var(--color-text-primary)" }}>{label}</option>;
                    })}
                  </AppSelect>
                ) : (
                  <div className="flex items-center gap-1 flex-wrap">
                    {(durationsList || fallbackDurations).map(d => {
                      const dNum = typeof d === 'string' ? parseInt(d) : d;
                      const label = dNum === -1 ? '智能时长' : (typeof d === 'string' ? d : `${d}s`);
                      return (
                        <Pill key={d} active={duration === dNum} onClick={() => onDurationChange(dNum)}>
                          {label}
                        </Pill>
                      );
                    })}
                  </div>
                )
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={durationRange[0]}
                    max={durationRange[1]}
                    step={1}
                    value={duration}
                    onChange={(e) => onDurationChange(Number(e.target.value))}
                    style={{ width: 160, accentColor: ACCENT }}
                  />
                  <span className="text-2xs font-medium font-mono-num" style={{ color: ACCENT, minWidth: 26 }}>{duration}s</span>
                </div>
              )}
            </Section>
          )}

          {/* ── 画幅 ── */}
          <Section title="画幅">
            <div className="flex items-center gap-1 flex-wrap">
              {aspectRatios.map(r => (
                <Pill key={r} active={aspectRatio === r} onClick={() => onAspectRatioChange(r)}>
                  {r}
                </Pill>
              ))}
            </div>
          </Section>

          {/* ── 分辨率 ── */}
          <Section title="分辨率">
            <div className="flex items-center gap-1 flex-wrap">
              {videoResolutions.map(r => (
                <Pill key={r} active={videoResolution === r} onClick={() => onVideoResolutionChange(r)}>
                  {r}
                </Pill>
              ))}
            </div>
          </Section>

          {seedance25Capabilities && (
            <Section title="2.5 生成方式">
              <div className="flex items-center gap-1 flex-wrap">
                {WANX_VIDEO_MODES.filter((mode) => seedance25Capabilities.modes.includes(mode.id)).map((mode) => (
                  <Pill key={mode.id} active={(seedance25Mode || "reference") === mode.id} mono={false} onClick={() => onSeedance25ModeChange(mode.id)}>{mode.name}</Pill>
                ))}
              </div>
              <p className="text-2xs leading-relaxed mt-2" style={{ color: "var(--color-text-muted)" }}>
                {seedance25Capabilities.referenceNotice || "参考素材按所选官方渠道匹配对应素材库。"}首尾帧按连线顺序读取前两张图片；切换线路不清空参考连线。
              </p>
              {seedance25Capabilities.supportsGenerateAudioControl && (
                <div className="mt-2"><Pill active={guaAudio !== false} mono={false} onClick={() => onGuaAudioChange(guaAudio === false)}>{guaAudio !== false ? "生成音频 开" : "生成音频 关"}</Pill></div>
              )}
            </Section>
          )}

          {/* ── Seedance 高级参数（默认折叠） ── */}
          {isSeedanceVideoProvider(provider) && (
            <div style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
              <details className="px-4 py-3.5">
                <summary className="text-label cursor-pointer select-none list-none flex items-center gap-1">
                  <ChevronDown size={10} className="details-chevron transition-transform" style={{ color: "var(--color-text-muted)" }} />
                  {providerConfig?.name || "视频生成"} 高级参数
                </summary>
                <div className="mt-2.5 space-y-2.5">
                  {/* 生成模式 */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>模式</span>
                    {GUA_VIDEO_MODES.map(m => (
                      <Pill key={m.id} active={guaVideoMode === m.id} mono={false} onClick={() => onGuaVideoModeChange(m.id)}>
                        {m.name}
                      </Pill>
                    ))}
                  </div>
                  {/* 生成音频 */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>音频</span>
                    <Pill active={!!guaAudio} mono={false} onClick={() => onGuaAudioChange(!guaAudio)}>
                      {guaAudio ? "生成音频 开" : "生成音频 关"}
                    </Pill>
                    {provider === "guavideo" && guaVideoMode === "t2v" && (
                      <Pill active={!!guaWebSearch} mono={false} onClick={() => onGuaWebSearchChange(!guaWebSearch)}>
                        {guaWebSearch ? "联网搜索 开" : "联网搜索 关"}
                      </Pill>
                    )}
                  </div>
                </div>
              </details>
            </div>
          )}

          {/* ── 万相 3.0 All-in-One 参数 ── */}
          {isWanx && (
            <div style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
              <details className="px-4 py-3.5" open>
                <summary className="text-label cursor-pointer select-none list-none flex items-center gap-1">
                  <ChevronDown size={10} className="details-chevron transition-transform" style={{ color: "var(--color-text-muted)" }} />
                  万相 3.0 生成方式
                </summary>
                <div className="mt-2.5 space-y-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    {WANX_VIDEO_MODES.map((mode) => (
                      <Pill
                        key={mode.id}
                        active={(wanxMode || "img2video") === mode.id}
                        mono={false}
                        onClick={() => onWanxModeChange(mode.id)}
                      >
                        {mode.name}
                      </Pill>
                    ))}
                  </div>
                  <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                    {wanxMode === "text2video"
                      ? "只使用提示词，不读取连入素材。"
                      : wanxMode === "start-end"
                        ? "按连线顺序读取前两张图片，分别作为首帧和尾帧。"
                        : wanxMode === "reference"
                          ? "连入的图片、视频和音频会按类型作为参考素材。"
                          : "读取第一张连入图片作为首帧。"}
                  </p>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>输出</span>
                    <Pill active={wanxAudio !== false} mono={false} onClick={() => onWanxAudioChange(wanxAudio === false)}>
                      {wanxAudio !== false ? "生成音频 开" : "生成音频 关"}
                    </Pill>
                    <Pill active={!!wanxWatermark} mono={false} onClick={() => onWanxWatermarkChange(!wanxWatermark)}>
                      {wanxWatermark ? "水印 开" : "水印 关"}
                    </Pill>
                  </div>
                  <label className="flex items-center gap-2">
                    <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>随机种子</span>
                    <input
                      type="number"
                      min={0}
                      max={2147483647}
                      step={1}
                      value={wanxSeed ?? ""}
                      onChange={(event) => onWanxSeedChange(event.target.value)}
                      placeholder="留空随机"
                      className="h-7 flex-1 rounded-md px-2 text-2xs outline-none font-mono-num"
                      style={{
                        color: "var(--color-text-primary)",
                        background: "var(--color-bg-tertiary)",
                        border: "1px solid var(--color-border-secondary)",
                      }}
                    />
                  </label>
                </div>
              </details>
            </div>
          )}

          {/* ── 打光 ── */}
          <Section title="打光">
            <div className="grid grid-cols-2 gap-1">
              {LIGHTING_PRESETS.map(lp => {
                const isSel = selectedLighting === lp.id;
                return (
                  <button
                    key={lp.id}
                    onClick={() => onLightingChange(isSel ? null : lp.id)}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left transition-colors"
                    style={{
                      color: isSel ? lp.color : "var(--color-text-secondary)",
                      background: isSel ? accentBg(lp.color, 10) : "transparent",
                      border: `1px solid ${isSel ? accentBg(lp.color, 35) : "var(--color-border-secondary)"}`,
                    }}
                    title={lp.prompt}
                  >
                    <span className="text-sm leading-none">{lp.emoji}</span>
                    <span className="text-2xs truncate flex-1">{lp.name}</span>
                    {isSel && <Check size={10} style={{ color: lp.color }} />}
                  </button>
                );
              })}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );

  // 节点容器带 transform（ReactFlow 定位/悬浮动画），fixed 弹框必须 portal 到 body
  return createPortal(modal, document.body);
}
